// Main-app mini guide registry.
// Each guide is a short 3–5 step walkthrough for a specific page.
// pageKey values match the ExplainerVideoButton pageKey on each page.
// Targets use data-tour="..." selectors added alongside each GuideButton.
import { ALL_ADMIN_GUIDES } from "@/data/admin-guides";
import { isHiddenSectionEnabled } from "@shared/hidden-sections";

export type TourPlacement = "top" | "bottom" | "left" | "right" | "auto";

export interface TourStep {
  target: string;
  title: string;
  body: string;
  placement?: TourPlacement;
  /** Optional CSS selector clicked before measuring `target` (e.g. a tab trigger). */
  activate?: string;
}

export interface TourConfig {
  id: string;
  steps: TourStep[];
  /**
   * Optional extra step injected right after the heading when the page was
   * reached from a specific property (i.e. the URL carries `?propertyId=`).
   * Several property-detail tabs (Members, Administrators, Financials,
   * Supplies) navigate to a shared portfolio-wide page that pre-filters to the
   * originating property. The standalone guide describes the portfolio view, so
   * this step clarifies that the current view is scoped to one property. When no
   * property context is present the guide runs unchanged (no regression).
   */
  propertyScopedStep?: TourStep;
  /**
   * Optional extra step appended to the end of the guide only when its `target`
   * is actually present in the DOM at guide-start. Used for audience-specific
   * panels that render for one role only — e.g. the Sell listing detail page is
   * shared by the listing owner (who sees a "Buyer Inquiries" inbox) and buyers
   * viewing the same listing (who see a "Contact Seller" CTA instead). The owner
   * step would point at nothing for a buyer, so MiniTourProvider injects it only
   * when the owner-only anchor exists, mirroring `propertyScopedStep`'s
   * conditional injection but keying off DOM presence rather than a URL param.
   */
  ownerScopedStep?: TourStep;
  /**
   * Optional extra step appended to the end of the guide only when its `target`
   * is present in the DOM at guide-start — the buyer-audience counterpart to
   * `ownerScopedStep`. On the shared Sell listing detail page the owner sees the
   * "Buyer Inquiries" inbox while a buyer viewing the same listing sees a
   * "Contact Seller" CTA instead; this step explains the buyer flow and is
   * injected only when the buyer-only anchor exists, so it points at nothing for
   * the owner. MiniTourProvider handles both DOM-presence-conditional steps with
   * the same generalized injection.
   */
  buyerScopedStep?: TourStep;
}

// ─── Individual page guides ──────────────────────────────────────────────────

const dashboardGuide: TourConfig = {
  id: "dashboard",
  steps: [
    {
      target: '[data-tour="dashboard-heading"]',
      title: "Your Dashboard",
      body: "This is your command center. At a glance you can see how many active properties, open maintenance tasks, unread messages, and pending approvals you have.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-metrics"]',
      title: "Key metrics",
      body: "These tiles give you the most important numbers across your portfolio. Click any tile to jump straight to that section.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-panels"]',
      title: "Activity panels",
      body: "Each panel below the metrics surfaces the latest activity — open maintenance tasks, unread notifications, thermostat health, and more.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-explainer"]',
      title: "Explainer videos and guides",
      body: "Use the Explainer Video and Guide buttons at the top of any page to learn more about that section. They are always available when you need a refresher.",
      placement: "left",
    },
  ],
};

const propertiesGuide: TourConfig = {
  id: "properties",
  steps: [
    {
      target: '[data-tour="properties-heading"]',
      title: "Your Properties",
      body: "Every property in your portfolio is listed here — active, in renovation, under underwriting, or archived. Use filters at the top to narrow the list.",
      placement: "bottom",
    },
    {
      target: '[data-tour="properties-table"]',
      title: "Property table",
      body: "Each row shows the property name, status, state, room count, active listings, group, PadSplit ID, and setup progress. Select a setup percentage to open that property's checklist.",
      placement: "top",
    },
    {
      target: '[data-tour="properties-add"]',
      title: "Add a property",
      body: "Use the Add Property button to onboard a new address. You can set the room count, status, and group right from the dialog.",
      placement: "bottom",
    },
    {
      target: '[data-tour="properties-create-groups"]',
      title: "Create Property Groups",
      body: "Property groups let you organize your portfolio into named collections — for example by city, market, or ownership entity. Click here to create and manage groups, then assign each property to one for easier filtering and reporting.",
      placement: "bottom",
    },
    {
      target: '[data-tour="properties-bulk-import-export"]',
      title: "Bulk Import / Export",
      body: "Add many properties or appliances at once from a spreadsheet, or export your current property list to a file — far faster than entering records one by one.",
      placement: "bottom",
    },
    {
      target: '[data-tour="properties-filters"]',
      title: "Filter and group",
      body: "Search your properties, filter by property group, or include archived properties with Show archived. Group by State or Property group to organize the table, or choose None for a flat list. Clear a custom sort to return to names A–Z.",
      placement: "bottom",
    },
  ],
};

const propertyDetailGuide: TourConfig = {
  id: "property-detail",
  steps: [
    {
      target: '[data-tour="property-detail-heading"]',
      title: "Property Detail",
      body: "This page brings the essentials for one home together. The Property Setup Checklist beside the name stays available above every tab and can be reopened at any time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="property-detail-tabs"]',
      title: "Tabs",
      body: "The eight main sections are Overview, Rooms, Members, Maintenance, Appliances, Devices, Documents, and Knowledge Base. When image-generation access is available, Prompts & Settings is also here. We'll walk through the main sections in order, including the Image Generator inside Rooms.",
      placement: "bottom",
    },
    {
      target: '[data-tour="property-detail-overview"]',
      activate: '[data-testid="tab-overview"]',
      title: "Overview",
      body: "The Overview tab shows the property at a glance — its map location, photo, address, and key details. It's the landing view whenever you open a property.",
      placement: "top",
    },
    {
      target: '[data-tour="property-detail-rooms"]',
      activate: '[data-testid="tab-rooms"]',
      title: "Rooms",
      body: "The Rooms tab lists every room with its number, type, and current occupancy status. You can add rooms, link them to shadow listings, and upload before/after photos here.",
      placement: "top",
    },
    {
      target: '[data-tour="property-detail-image-generator"]',
      activate: '[data-testid="tab-rooms"]',
      title: "Image Generator",
      body: "Click Image Generation to use AI to generate beautiful, fully-furnished bedroom photos for each room — high-converting visuals that attract more prospects to your listing.",
      placement: "bottom",
    },
    {
      target: '[data-tour="property-detail-members"]',
      title: "Members",
      body: "The Members tab opens the Members page filtered to this property, where you can see who's living here, their lease and move-in details, and their contact info.",
      placement: "bottom",
    },
    {
      target: '[data-tour="property-detail-maintenance"]',
      activate: '[data-testid="tab-maintenance"]',
      title: "Maintenance",
      body: "The Maintenance tab tracks repair and upkeep tasks for this property — log issues, assign work, and follow them through to completion.",
      placement: "top",
    },
    {
      target: '[data-tour="property-detail-appliances"]',
      activate: '[data-testid="tab-appliances"]',
      title: "Appliances",
      body: "The Appliances tab keeps the property's equipment inventory together, including make, model, serial number, warranty, and purchase details.",
      placement: "top",
    },
    {
      target: '[data-tour="property-detail-devices"]',
      activate: '[data-testid="tab-devices"]',
      title: "Devices",
      body: "Manage smart locks and thermostats linked to this property. Connect devices, review settings, and adjust temperature alert thresholds.",
      placement: "top",
    },
    {
      target: '[data-tour="property-detail-documents"]',
      activate: '[data-testid="tab-documents"]',
      title: "Documents",
      body: "Store invoices, receipts, bids, contracts, and other files tied to this property.",
      placement: "top",
    },
    {
      target: '[data-tour="property-detail-kb"]',
      activate: '[data-testid="tab-knowledge-base"]',
      title: "Knowledge Base",
      body: "Keep practical, property-specific guidance here — Wi-Fi details, house rules, appliance instructions, and emergency contacts.",
      placement: "top",
    },
  ],
};

const messagesGuide: TourConfig = {
  id: "messages",
  steps: [
    {
      target: '[data-tour="messages-heading"]',
      title: "Messages",
      body: "All member conversations are here. Each conversation is tied to a member and property, so context is always visible alongside the chat.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-list"]',
      activate: '[data-testid="tab-inbox"]',
      title: "Conversation list",
      body: "Conversations are sorted by most recent message, with unread and unresolved threads flagged. Click any conversation to open it in the panel on the right.",
      placement: "right",
    },
    {
      target: '[data-tour="messages-search"]',
      activate: '[data-testid="tab-inbox"]',
      title: "Find & filter conversations",
      body: "Search by member name or message content to jump straight to a thread. Toggle \"Unresolved messages only\" to focus on open queues, and once you've searched you can narrow results to the last 7, 30, or 90 days.",
      placement: "right",
    },
    {
      target: '[data-tour="messages-compose"]',
      activate: '[data-testid^="row-inbox-"]',
      title: "Reply and compose",
      body: "Type in the reply box and hit Send. You can attach files by dragging them in, insert saved templates, and use Ctrl/⌘+Enter to send without leaving the keyboard.",
      placement: "top",
    },
    {
      target: '[data-tour="messages-canned-picker"]',
      activate: '[data-testid^="row-inbox-"]',
      title: "Insert a canned response",
      body: "Click this bookmark button next to the reply box to pick from your saved canned responses and instantly insert the text into your draft. You can also type \"/\" in the message box to search them inline.",
      placement: "top",
    },
    {
      target: '[data-tour="messages-schedule"]',
      activate: '[data-testid^="row-inbox-"]',
      title: "Schedule a message",
      body: "Need to send later? Click the clock to pick a date and time, and the message goes out automatically. Anything you schedule shows up under the Scheduled tab, where you can review or cancel it before it sends.",
      placement: "top",
    },
    {
      target: '[data-tour="messages-member-info"]',
      activate: '[data-testid^="row-inbox-"]',
      title: "Member info panel",
      body: "Use this toggle to show or hide the member info panel beside the conversation. It surfaces contact details, housing and room, Wi-Fi, lock PINs, vehicle, and emergency contacts — and collapses to give the chat more room.",
      placement: "left",
    },
    {
      target: '[data-tour="messages-awaiting"]',
      activate: '[data-testid="tab-awaiting"]',
      title: "Drafts",
      body: "The Drafts tab collects messages waiting on you — auto-rule and AI-suggested replies that need a quick review before they send. Approve or dismiss each one from here.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-broadcast"]',
      title: "Broadcast",
      body: "Send the same message to everyone in a property — or your whole portfolio — at once. Great for house-wide announcements like maintenance windows, policy updates, or seasonal reminders.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-settings"]',
      title: "Rules & canned responses",
      body: "Open Settings to find \"Rules and Canned Responses.\" Rules auto-send messages when events happen — move-in, rent due, maintenance complete, and more. Canned responses are saved templates your whole team can insert in one click, with {{member_first_name}}, {{member_full_name}}, and {{property_name}} placeholders.",
      placement: "bottom",
    },
  ],
};

const financialsGuide: TourConfig = {
  id: "financials",
  propertyScopedStep: {
    target: '[data-testid="link-back-to-property"]',
    title: "Scoped to one property",
    body: "You opened Financials from a property, so the P&L and expenses below are already filtered to that property. Widen the property filter to see your whole portfolio, or use \"Back to …\" to return to the property.",
    placement: "bottom",
  },
  steps: [
    {
      target: '[data-tour="financials-heading"]',
      title: "Financials",
      body: "Track revenue, expenses, and net payouts across your entire portfolio or for a single property. Switch between the P&L tab, expense ledger, and settings using the tabs above.",
      placement: "bottom",
    },
    {
      target: '[data-tour="financials-tabs"]',
      title: "Tabs",
      body: "Balance shows your live Mercury account balances. The P&L tab shows a month-by-month breakdown of collections vs. expenses. Expenses lists every individual expense, including any auto-imported from Mercury. Forwarded Docs is your financial document inbox. Settings lets you manage categories and Mercury Connect.",
      placement: "bottom",
    },
    {
      target: '[data-tour="financials-filters"]',
      activate: '[data-testid="tab-pnl"]',
      title: "Filter by property and time range",
      body: "Inside the P&L tab, narrow the view to a single property or look at all properties together. Change the time range to see the last 3, 6, or 12 months of data.",
      placement: "bottom",
    },
    {
      target: '[data-tour="financials-export"]',
      activate: '[data-testid="tab-pnl"]',
      title: "Export",
      body: "Download the P&L or expense list as a CSV or PDF for bookkeeping, your accountant, or tax prep.",
      placement: "bottom",
    },
  ],
};

const membersGuide: TourConfig = {
  id: "members",
  propertyScopedStep: {
    target: '[data-testid="link-back-to-property"]',
    title: "Scoped to one property",
    body: "You opened Members from a property, so the list below is already filtered to that property's members. Widen the property filter to see members across your portfolio, or use \"Back to …\" to return to the property.",
    placement: "bottom",
  },
  steps: [
    {
      target: '[data-tour="members-heading"]',
      title: "Members",
      body: "Every co-living member across all your properties is listed here — both direct members you manage manually and PadSplit members synced from the platform.",
      placement: "bottom",
    },
    {
      target: '[data-tour="members-table"]',
      title: "Member table",
      body: "Click a member row to open their detail page with lease info, payment history, and messaging. Sort by name, room, rating, late payment frequency, or tenure.",
      placement: "top",
    },
    {
      target: '[data-tour="members-add"]',
      title: "Add a member",
      body: "Use the Add Member button to manually onboard a direct member. Select the property and room, and choose their status and type.",
      placement: "bottom",
    },
    {
      target: '[data-tour="members-filters"]',
      title: "Filter by property and type",
      body: "Narrow the list to a single property, filter by Direct vs. PadSplit members, or search by name, email, or phone.",
      placement: "bottom",
    },
  ],
};

const maintenanceGuide: TourConfig = {
  id: "maintenance",
  steps: [
    {
      target: '[data-tour="maintenance-heading"]',
      title: "Maintenance",
      body: "All maintenance tasks across your portfolio live here — submitted by members, created by you, or generated automatically from rules.",
      placement: "bottom",
    },
    {
      target: '[data-tour="maintenance-tasks"]',
      title: "Task list",
      body: "Each task card shows the property, room, priority, status, and assigned vendor. Click a task to open the detail view where you can message the vendor and track progress.",
      placement: "top",
    },
    {
      target: '[data-tour="maintenance-add"]',
      title: "Create a task",
      body: "Use the New Task button to create a maintenance request. Assign it to a vendor, set a priority, and attach photos — all in one step.",
      placement: "bottom",
    },
    {
      target: '[data-tour="maintenance-filters"]',
      title: "Filter by status and property",
      body: "Filter tasks by status (open, in progress, complete), priority, property, or assigned vendor to focus on what needs attention now.",
      placement: "bottom",
    },
  ],
};

const vendorsGuide: TourConfig = {
  id: "vendors",
  steps: [
    {
      target: '[data-tour="vendors-heading"]',
      title: "Vendors",
      body: "Your vendor network — plumbers, electricians, cleaners, and more — is managed here. Invite vendors so they can receive tasks and get paid directly through the platform.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendors-list"]',
      title: "Vendor list",
      body: "Each vendor card shows their name, business, service categories, rating, and number of completed tasks. Click a vendor to see their full profile and task history.",
      placement: "top",
    },
    {
      target: '[data-tour="vendors-invite"]',
      title: "Invite a vendor",
      body: "Click Add Vendor to invite a contractor. They will receive an SMS with a link to create their Vendor Portal account where they can accept jobs and get paid.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendors-categories"]',
      title: "Service categories",
      body: "Filter vendors by service category — plumbing, electrical, HVAC, cleaning, and more — so you can quickly find the right contractor for each job.",
      placement: "bottom",
    },
  ],
};

const devicesGuide: TourConfig = {
  id: "devices",
  steps: [
    {
      target: '[data-tour="devices-heading"]',
      title: "Devices",
      body: "Smart locks and thermostats connected to your properties are managed here. Connect your lock accounts to start monitoring and controlling devices remotely.",
      placement: "bottom",
    },
    {
      target: '[data-tour="devices-locks"]',
      title: "Smart Locks",
      body: "Generate and revoke access codes for members, vendors, or one-time guests. Lock status and battery levels are shown in real time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="devices-thermostats"]',
      title: "Thermostats",
      body: "Monitor temperature and set-point health for every connected thermostat. Out-of-range alerts fire automatically when a thermostat drifts beyond your threshold.",
      placement: "bottom",
    },
    {
      target: '[data-tour="devices-members"]',
      title: "Members & access",
      body: "The Members tab shows every member alongside their door codes (PIN), when each code activates, and when it was last used — so you can see who has access at a glance across all your locks.",
      placement: "bottom",
    },
  ],
};

const teamGuide: TourConfig = {
  id: "team",
  propertyScopedStep: {
    target: '[data-testid="link-back-to-property"]',
    title: "Scoped to one property",
    body: "You opened Administrators from a property, so the team list is filtered to who has access to that property. Widen the property filter to see your whole team, or use \"Back to …\" to return to the property.",
    placement: "bottom",
  },
  steps: [
    {
      target: '[data-tour="team-heading"]',
      title: "Team",
      body: "Invite teammates to help manage your portfolio. Each team member gets their own login and a role that controls what they can see and do.",
      placement: "bottom",
    },
    {
      target: '[data-tour="team-list"]',
      title: "Team members",
      body: "Each card shows the member's name, role, and permission areas. Roles range from View-only to Admin. The account owner has full access and cannot be demoted.",
      placement: "top",
    },
    {
      target: '[data-tour="team-invite"]',
      title: "Invite a teammate",
      body: "Click Invite Member to add a teammate by email. They will receive an invitation to create their account and will land directly in your team's workspace.",
      placement: "bottom",
    },
    {
      target: '[data-tour="team-permissions"]',
      title: "Permissions",
      body: "Permissions let you control which sections each role can edit vs. view-only. Customize per-role access to properties, members, financials, maintenance, and more.",
      placement: "top",
    },
  ],
};

const collectionsGuide: TourConfig = {
  id: "collections",
  steps: [
    {
      target: '[data-tour="collections-heading"]',
      title: "Collections",
      body: "Automatically offer payment arrangements to members who have fallen behind. Collections is off until you turn it on and choose which properties it runs at.",
      placement: "bottom",
    },
    {
      target: '[data-tour="collections-properties-list"]',
      title: "Choose your properties",
      body: "Check each property collections should run at. Every property can override the account rules — its own balance threshold, payment percentage, or cooldown.",
      placement: "top",
    },
    {
      target: '[data-tour="collections-filters"]',
      title: "Filters",
      body: "Search, or filter by property group to review every property belonging to one owner at a time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="collections-process"]',
      title: "Preview before you send",
      body: "Preview shows exactly who would be contacted without sending anything. Run now processes every enabled property immediately.",
      placement: "bottom",
    },
    {
      target: '[data-tour="collections-list"]',
      activate: '[data-testid="tab-activity"]',
      title: "Activity log",
      body: "Every member the engine evaluated — messages sent, plus anyone skipped and why. Filter by property or member, and jump straight to the conversation.",
      placement: "top",
    },
  ],
};

const maintenanceSettingsGuide: TourConfig = {
  id: "maintenance-settings",
  steps: [
    {
      target: '[data-tour="maintenance-settings-heading"]',
      title: "Maintenance Settings",
      body: "Configure how maintenance tasks are handled — default priorities, vendor assignment rules, and notification preferences.",
      placement: "bottom",
    },
    {
      target: '[data-tour="maintenance-settings-rules"]',
      title: "Task rules",
      body: "Automatically create maintenance tasks when specific events happen, such as a member move-out triggering a cleaning task.",
      placement: "top",
    },
    {
      target: '[data-tour="maintenance-settings-notifications"]',
      title: "Notifications",
      body: "Choose who gets notified when a task is created, updated, or completed — the assigned vendor, the property manager, or both.",
      placement: "top",
    },
  ],
};

const accountPlanGuide: TourConfig = {
  id: "account-plan",
  steps: [
    {
      target: '[data-tour="account-plan-heading"]',
      title: "Plan & Modules",
      body: "Build your plan here: pick a base tier, stack on exactly the modules you use, and see the live bill update as you go.",
      placement: "bottom",
    },
    {
      target: '[data-tour="account-plan-calculator"]',
      title: "The live calculator",
      body: "Your room, owner, and private-vendor counts come from your live account. Changes apply to your subscription immediately with automatic proration.",
      placement: "top",
    },
  ],
};

const billingGuide: TourConfig = {
  id: "billing",
  steps: [
    {
      target: '[data-tour="billing-heading"]',
      title: "Account & Billing",
      body: "Manage your PadPulse subscription, view invoices, and update your payment method here.",
      placement: "bottom",
    },
    {
      target: '[data-tour="billing-plan"]',
      title: "Your plan",
      body: "Your current plan tier and the features it includes are shown here. Upgrade to unlock additional properties, team members, or premium features.",
      placement: "bottom",
    },
    {
      target: '[data-tour="billing-invoices"]',
      title: "Invoices",
      body: "Every past invoice is listed here with its amount, date, and download link. Useful for expense reporting.",
      placement: "top",
    },
  ],
};

const profileGuide: TourConfig = {
  id: "profile",
  steps: [
    {
      target: '[data-tour="profile-heading"]',
      activate: '[data-tour="tab-btn-personal"]',
      title: "Personal info",
      body: "This is your profile. Start here to keep your name, email, and phone current — it's used for account recovery and for notifications sent to you as the account owner.",
      placement: "bottom",
    },
    {
      target: '[data-tour="profile-notifications-occupancy"]',
      activate: '[data-tour="tab-btn-notifications"]',
      title: "Notifications",
      body: "Many people want to exclude inactive rooms from their occupancy calculation as newly launched properties or rooms under renovations can cause their PadSplit Occupancy Percentage to be inaccurate. Toggle here.",
      placement: "top",
    },
    {
      target: '[data-tour="profile-security-2fa"]',
      activate: '[data-tour="tab-btn-security"]',
      title: "Two-factor authentication",
      body: "We highly recommend turning on two-factor authentication for an extra layer of account protection.",
      placement: "top",
    },
    {
      target: '[data-tour="profile-security-password"]',
      activate: '[data-tour="tab-btn-security"]',
      title: "Password",
      body: "Add a password so you can sign in directly instead of waiting for an email code every time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="profile-security-devices"]',
      activate: '[data-tour="tab-btn-security"]',
      title: "Active sessions & trusted devices",
      body: "Review your active sessions and manage trusted devices here. Sign-ins from a device you haven't trusted trigger a security alert so you always know when someone new accesses your account.",
      placement: "top",
    },
    {
      target: '[data-tour="profile-appearance-theme"]',
      activate: '[data-tour="tab-btn-appearance"]',
      title: "Appearance",
      body: "Choose how PadPulse looks — Light, Dark, or System (which follows your device). Switch on dark mode here if you prefer a darker interface; your choice is saved to your account.",
      placement: "top",
    },
    {
      target: '[data-tour="profile-early-access-panel"]',
      activate: '[data-tour="tab-btn-early-access"]',
      title: "Early Access",
      body: "Opt in to experimental beta features before they're released to everyone from this panel. You can leave any program at any time.",
      placement: "top",
    },
  ],
};

const smartLocksGuide: TourConfig = {
  id: "smart-locks",
  steps: [
    {
      target: '[data-tour="smart-locks-heading"]',
      title: "Smart Locks",
      body: "This page is where you connect your lock accounts and configure access-code defaults. Per-lock status and access-code management live under Devices → Smart Locks and on each property page.",
      placement: "bottom",
    },
    {
      target: '[data-tour="smart-locks-connect"]',
      title: "Connect Account",
      body: "Click here to link a new TTLock or other-brand account. You can connect multiple accounts — one per lock brand or credential set.",
      placement: "bottom",
    },
    {
      target: '[data-tour="smart-locks-list"]',
      title: "TTLock Accounts",
      body: "TTLock (and Sifely) accounts are free with your PadPulse subscription. Each connected account appears as its own card here, with controls to reconnect, sync, or disconnect.",
      placement: "top",
    },
    {
      target: '[data-tour="smart-locks-other-brands"]',
      title: "Other Brand Accounts",
      body: "Connect August, Yale, Schlage, and other non-TTLock brands. A $2.50/lock/month fee applies. Expand a row to view and manage individual locks under that account.",
      placement: "top",
    },
    {
      target: '[data-tour="smart-locks-settings-code-length"]',
      title: "Default Code Length",
      body: "Choose whether access codes are 4, 5, or 6 digits. This length is used whenever PadPulse auto-generates a code for a new member.",
      placement: "top",
      activate: '[data-tour="smart-locks-tab-settings"]',
    },
    {
      target: '[data-tour="smart-locks-settings-low-battery"]',
      title: "Low Battery Auto-Notification",
      body: "Toggle automatic messages to the member assigned to a lock when its battery drops below your chosen threshold. One notification is sent per low-battery episode so members aren't spammed.",
      placement: "top",
      activate: '[data-tour="smart-locks-tab-settings"]',
    },
  ],
};

const thermostatsGuide: TourConfig = {
  id: "thermostats",
  steps: [
    {
      target: '[data-tour="thermostats-heading"]',
      title: "Thermostats",
      body: "Monitor and manage smart thermostats across all your properties from one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="thermostats-list"]',
      title: "Thermostat list",
      body: "Each card shows the current temperature, set-point, HVAC mode, and online status. Out-of-range devices are highlighted in amber.",
      placement: "top",
    },
    {
      target: '[data-tour="thermostats-health"]',
      title: "Health monitoring",
      body: "Configure a temperature threshold per property. If a thermostat drifts beyond that threshold, you'll get an in-app alert automatically.",
      placement: "top",
      activate: '[data-testid="subtab-thermostats-devices"]',
    },
  ],
};

const memberDetailGuide: TourConfig = {
  id: "member-detail",
  steps: [
    {
      target: '[data-tour="member-detail-heading"]',
      title: "Member Detail",
      body: "Everything about one member is here — contact info, current room and property, lease history, payment status, and their conversation thread.",
      placement: "bottom",
    },
    {
      target: '[data-tour="member-detail-info"]',
      title: "Member info",
      body: "View and edit the member's name, email, phone, type (Direct or PadSplit), status, and assigned room.",
      placement: "bottom",
    },
    {
      target: '[data-tour="member-detail-history"]',
      title: "Lease history",
      body: "The history panel shows every property and room this member has been in, along with move-in and move-out dates.",
      placement: "top",
    },
    {
      target: '[data-tour="member-detail-messages"]',
      title: "Messaging",
      body: "The Messages tab shows every conversation thread with this member. Reply directly from here without leaving the member's profile.",
      placement: "top",
    },
  ],
};

const bookingRequestsGuide: TourConfig = {
  id: "booking-requests",
  steps: [
    {
      target: '[data-tour="booking-requests-heading"]',
      title: "Booking Requests",
      body: "Prospective members who submit a booking request from your direct booking page appear here for you to review and approve or decline.",
      placement: "bottom",
    },
    {
      target: '[data-tour="booking-requests-settings"]',
      title: "Request list",
      body: "Each card shows the applicant's name, the property and room they applied for, and how long ago they applied. Open a request to see their full application.",
      placement: "top",
    },
    {
      target: '[data-tour="booking-requests-actions"]',
      title: "Approve or decline",
      body: "Review the applicant's details, then approve or decline. Approving sends them an SMS with move-in instructions and creates their member record automatically.",
      placement: "top",
    },
  ],
};

const bookingRequestSettingsGuide: TourConfig = {
  id: "booking-request-settings",
  steps: [
    {
      target: '[data-tour="booking-request-settings-heading"]',
      title: "Booking Request Settings",
      body: "Configure how your direct booking form looks and what information it collects from applicants.",
      placement: "bottom",
    },
    {
      target: '[data-tour="booking-request-settings-form"]',
      title: "Form fields",
      body: "Choose which fields are required on the booking form — name, email, phone, employment status, move-in date, and more.",
      placement: "top",
    },
    {
      target: '[data-tour="booking-request-settings-form"]',
      title: "Your booking link",
      body: "Copy your unique booking link and share it on social media, listing sites, or in messages so prospects can self-apply.",
      placement: "bottom",
    },
  ],
};

const vehiclesGuide: TourConfig = {
  id: "vehicles",
  steps: [
    {
      target: '[data-tour="vehicles-heading"]',
      title: "Vehicles",
      body: "Track member vehicles — make, model, color, and license plate — so you always know whose car is parked at each property.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vehicles-list"]',
      title: "Vehicle list",
      body: "Each row links a vehicle to a member and property. Filter by property to generate a parking roster.",
      placement: "top",
    },
    {
      target: '[data-tour="vehicles-add"]',
      title: "Add a vehicle",
      body: "Click Add Vehicle to register a new vehicle. You can link it to an existing member or add it standalone for a property.",
      placement: "bottom",
    },
  ],
};

const vendorDetailGuide: TourConfig = {
  id: "vendor-detail",
  steps: [
    {
      target: '[data-tour="vendor-detail-heading"]',
      title: "Vendor Detail",
      body: "View a vendor's full profile — their contact info, service categories, rating, and complete task history with your properties.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendor-detail-info"]',
      title: "Vendor info",
      body: "Edit the vendor's name, phone, business name, and service categories. Keeping categories current ensures they appear in the right filter when assigning tasks.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendor-detail-tasks"]',
      title: "Task history",
      body: "Every task assigned to this vendor is listed here with its property, status, and date. Click a task to see the full detail including photos and messages.",
      placement: "top",
    },
  ],
};

const vendorSettingsGuide: TourConfig = {
  id: "vendor-settings",
  steps: [
    {
      target: '[data-tour="vendor-settings-heading"]',
      title: "Vendor Settings",
      body: "Configure global vendor preferences — default payment terms, approval workflows, and notification settings for vendor-related events.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendor-settings-categories"]',
      title: "Service categories",
      body: "Define the custom service categories your vendors are assigned to, so you can filter and assign vendors quickly.",
      placement: "top",
    },
    {
      target: '[data-tour="vendor-settings-categories"]',
      title: "Notifications",
      body: "Choose who gets notified when a vendor submits a completed task, requests payment, or updates a job status.",
      placement: "top",
    },
  ],
};

const vendorMarketplaceGuide: TourConfig = {
  id: "vendor-marketplace",
  steps: [
    {
      target: '[data-tour="vendor-marketplace-heading"]',
      title: "Vendor Marketplace",
      body: "Browse vendors who have opted into the public PadPulse Vendor Marketplace. Hire them directly for your properties even if they haven't worked with you before.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendor-marketplace-list"]',
      title: "Browse vendors",
      body: "Each card shows the vendor's business name, location, service categories, and aggregate rating from other hosts.",
      placement: "top",
    },
    {
      target: '[data-tour="vendor-marketplace-filters"]',
      title: "Filter by service",
      body: "Filter by service category and location to find the right vendor for a specific job near your property.",
      placement: "bottom",
    },
  ],
};

const vendorPaymentsGuide: TourConfig = {
  id: "vendor-payments",
  steps: [
    {
      target: '[data-tour="vendor-payments-heading"]',
      title: "Vendor Payments & Approvals",
      body: "Review and approve vendor payment requests here before funds are released. Completed tasks that require payment show up in this queue.",
      placement: "bottom",
    },
    {
      target: '[data-tour="vendor-payments-list"]',
      title: "Approval queue",
      body: "Each row shows the vendor, task, amount, and submission date. Click Approve to authorize payment or Reject to send it back.",
      placement: "top",
      activate: '[data-testid="tab-payment-history"]',
    },
    {
      target: '[data-tour="vendor-payments-filters"]',
      title: "Filter by status",
      body: "Filter by pending, approved, or rejected to focus on what needs a decision right now.",
      placement: "bottom",
    },
  ],
};

const operationsPaymentsGuide: TourConfig = {
  id: "operations-payments",
  steps: [
    {
      target: '[data-tour="operations-payments-heading"]',
      title: "Operations Payments",
      body: "Track all vendor payments that have been approved and processed. This is your full payment ledger for vendor work.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-payments-list"]',
      title: "Payment history",
      body: "Each row shows the vendor, task, amount paid, platform fee, and payout status. Filter by vendor or property to reconcile specific accounts.",
      placement: "top",
    },
    {
      target: '[data-tour="operations-payments-export"]',
      title: "Export",
      body: "Download the payment ledger as a CSV for your bookkeeper or accountant.",
      placement: "bottom",
    },
  ],
};

const serviceFormsGuide: TourConfig = {
  id: "service-forms",
  steps: [
    {
      target: '[data-tour="service-forms-heading"]',
      title: "Service Forms",
      body: "Create and send custom forms to vendors — inspection checklists, move-out condition reports, quote requests, and more.",
      placement: "bottom",
    },
    {
      target: '[data-tour="service-forms-list"]',
      title: "Form templates",
      body: "Your saved form templates are listed here. Each template can be sent to one or more vendors with a single click.",
      placement: "top",
      activate: '[data-testid="tab-links"]',
    },
    {
      target: '[data-tour="service-forms-create"]',
      title: "Create a form",
      body: "Click New Form to build a custom questionnaire. Add text, photo upload, checkbox, and signature fields using the drag-and-drop builder.",
      placement: "bottom",
    },
    {
      target: '[data-tour="service-forms-submissions"]',
      activate: '[data-testid="tab-submissions"]',
      title: "Submissions",
      body: "Completed forms submitted by vendors appear in the Submissions tab. Each submission has its own chat thread for follow-up.",
      placement: "top",
    },
  ],
};

// Member Cleaning System (Build 2) — the roster/rotation calendars.
const cleaningGuide: TourConfig = {
  id: "cleaning",
  steps: [
    {
      target: '[data-tour="cleaning-heading"]',
      title: "Cleaning Program",
      body: "Member self-cleaning schedules, generated automatically from who actually lives in each house — plus one-off professional cleaner visits when you need them. Each property picks a Daily Duty Roster or a Weekly Whole-House Rotation.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-dashboard"]',
      title: "Today at a glance",
      body: "The stat strip rolls up your whole portfolio — cleanings due today and this week, uncovered slots that need attention, proof forms you're still waiting on, and your 30-day completion rate.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-setup"]',
      title: "Set up a program",
      body: "Start here. Search any property by name or address to create or edit its cleaning program — properties that already have one are marked Active or Add-on off. Picking a property opens its full settings: model, covered days, standing days, opt-outs, and the proof form.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-visit"]',
      title: "One-off cleaner visits",
      body: "Need a professional clean between member turns? Book an as-needed visit with one of your eligible vendors — it's created as a normal vendor task, so dispatch, chat, and payment work exactly like any other job.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-portfolio"]',
      title: "Portfolio timeline",
      body: "Every property's roster at a glance — one block per member per day. Green means proof was submitted, red is past due with no proof, amber is an uncovered slot (a departed member or empty room), and violet bands are vendor cleaner visits. The legend under the grid decodes every color.",
      placement: "top",
    },
    {
      target: '[data-tour="cleaning-search"]',
      title: "Find a property",
      body: "Managing a lot of houses? Type part of a name or address to filter the timeline down to just the rows you care about, and use the date range next to it to look ahead or review what already happened.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-week"]',
      title: "Calendar tab",
      body: "The Calendar tab drills into a single property — a stacked 12-week view you can page through week by week.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-program-settings"]',
      activate: '[data-testid="tab-cleaning-property"]',
      title: "Working the calendar",
      body: "Pick a property, flip between weeks, and open Program to change the model, covered days, standing-day assignments, or proof form. Click any block to message the member, copy their proof link, or mark the slot complete or skipped.",
      placement: "bottom",
    },
    {
      target: '[data-tour="cleaning-received"]',
      activate: '[data-testid="tab-cleaning-received"]',
      title: "Forms received",
      body: "Every proof form a member submits lands in this queue — with photos, timestamps, and a location flag if the submission came from somewhere unexpected. Open one to review it, or jump straight into the member's message thread.",
      placement: "top",
    },
    {
      target: '[data-tour="cleaning-form-link"]',
      activate: '[data-testid="tab-cleaning-received"]',
      title: "The cleaning proof form",
      body: "The form itself lives in Forms. Use this link to build or edit your cleaning checklist — photo requirements, checkboxes, notes — then attach it to a program in the setup dialog so members get it automatically.",
      placement: "bottom",
    },
  ],
};

const operationsTaskRulesGuide: TourConfig = {
  id: "operations-task-rules",
  steps: [
    {
      target: '[data-tour="operations-task-rules-heading"]',
      title: "Task Rules",
      body: "Automate task creation based on events — member move-out triggers a cleaning task, lease renewal triggers an inspection, and so on.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-task-rules-list"]',
      title: "Active rules",
      body: "Each rule card shows the trigger event, the task it creates, the default vendor assignment, and whether the rule is active.",
      placement: "top",
    },
    {
      target: '[data-tour="operations-task-rules-add"]',
      title: "Add a rule",
      body: "Click Add Rule to configure a new automation. Choose the trigger event, the task template, and the default vendor or assignment logic.",
      placement: "bottom",
    },
  ],
};

const furnishingGuide: TourConfig = {
  id: "furnishing",
  steps: [
    {
      target: '[data-tour="furnishing-heading"]',
      title: "Furnishing Catalog",
      body: "Browse and manage your furnishing catalog — all the items you use to furnish your co-living rooms, from beds and dressers to lamps and curtains.",
      placement: "bottom",
    },
    {
      target: '[data-tour="furnishing-catalog-list"]',
      title: "Item catalog",
      body: "Each item shows the name, category, estimated cost, vendor, and a photo. Click an item to edit its details or view purchase history.",
      placement: "top",
    },
    {
      target: '[data-tour="furnishing-catalog-add"]',
      title: "Add an item",
      body: "Click Add Item to add a new product to the catalog. Attach a photo and a vendor link so ordering is fast next time.",
      placement: "bottom",
    },
  ],
};

const furnishingPropertyGuide: TourConfig = {
  id: "furnishing-property",
  steps: [
    {
      target: '[data-tour="furnishing-property-heading"]',
      title: "Property Furnishing",
      body: "Plan and track the furnishing status for each room in this property — what's ordered, what's been delivered, and what's still needed.",
      placement: "bottom",
    },
    {
      target: '[data-tour="furnishing-property-rooms"]',
      activate: '[data-testid="tab-furnishing-rooms"]',
      title: "Room list",
      body: "Each room shows its current furnishing status and a checklist of required items. Click a room to mark items as ordered or delivered.",
      placement: "top",
    },
    {
      target: '[data-tour="furnishing-property-orders"]',
      activate: '[data-testid="tab-furnishing-rooms"]',
      title: "Orders",
      body: "Track purchase orders linked to this property. Each order shows the vendor, item list, estimated delivery date, and cost.",
      placement: "top",
    },
  ],
};

const renovationsGuide: TourConfig = {
  id: "renovations",
  steps: [
    {
      target: '[data-tour="renovations-heading"]',
      title: "Renovations",
      body: "Plan and track renovation projects across your portfolio — from budget estimates to contractor assignments and progress milestones.",
      placement: "bottom",
    },
    {
      target: '[data-tour="renovations-list"]',
      title: "Project list",
      body: "Each project card shows the property, status, estimated budget, and percent complete. Click a project to see line-item details and assign vendors.",
      placement: "top",
    },
    {
      target: '[data-tour="renovations-add"]',
      title: "New project",
      body: "Click New Project to start tracking a renovation. Add line items, attach permits and plans, and link it to your property.",
      placement: "bottom",
    },
    {
      target: '[data-tour="renovations-defaults-panel"]',
      title: "Defaults",
      body: "Pre-fill estimated days for each renovation item type and set a default permit timeline. These values auto-populate when you add items to any new renovation plan — saving you from entering the same numbers every time.",
      placement: "top",
      activate: '[data-testid="tab-renovations-defaults"]',
    },
    {
      target: '[data-tour="renovations-settings-panel"]',
      title: "Settings",
      body: "Control which team member roles are allowed to unlock a locked renovation. Locking a renovation prevents accidental edits to days and budgets once a project is underway.",
      placement: "top",
      activate: '[data-testid="tab-renovations-settings"]',
    },
  ],
};

const buyGuide: TourConfig = {
  id: "buy",
  steps: [
    {
      target: '[data-tour="buy-heading"]',
      title: "Buy",
      body: "The Buy page aggregates investment properties from three deal sources: the PadPulse Marketplace (host-to-host sales), Wholesale Deals (off-market properties from verified wholesaler partners), and Realtor Listings (MLS and partner-realtor properties). Each source has its own Connect flow to reach the right party.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-filters"]',
      title: "Source filter bar",
      body: "Use these tabs to focus on a specific source. All Sources shows everything at once. PadPulse Marketplace shows only host-listed properties. Wholesale Deals shows only wholesaler submissions. Realtor Listings shows only partner-realtor listings. My Inquiries shows your active Connect conversations.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-marketplace-section"]',
      title: "PadPulse Marketplace",
      body: "These properties are listed for sale by other PadPulse hosts — existing co-living operations you can buy and inherit. Click a listing and use 'Inquire about this property' to start a direct chat with the host-seller.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-wholesale-section"]',
      title: "Wholesale Deals",
      body: "Click this tab to browse off-market deals submitted by verified wholesaler partners — typically below-market properties requiring some work. On any wholesale card, click 'I'm Interested' to send a message directly to the wholesaler.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-realtor-section"]',
      title: "Realtor Listings",
      body: "MLS and off-market listings sourced by partner realtors who specialize in co-living. Click 'Request Showing' on any realtor card to send a showing request directly to the listing realtor.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-find-realtor"]',
      title: "Find a Realtor",
      body: "Need help finding a property? Use this button to browse the directory of Co-Living Certified partner realtors. Send them a message to start working together on your acquisition.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-inquiries-filter"]',
      title: "My Inquiries",
      body: "Every Connect conversation you start — whether inquiring about a marketplace listing, expressing interest in a wholesale deal, or requesting a realtor showing — lands here. Click My Inquiries to pick up where you left off.",
      placement: "bottom",
    },
  ],
};

const buyDetailGuide: TourConfig = {
  id: "buy-detail",
  steps: [
    {
      target: '[data-tour="buy-detail-heading"]',
      title: "Listing overview",
      body: "The address, location, and source sit at the top, with the headline metrics just below — purchase price, cash-on-cash, monthly cash flow, and cap rate.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-detail-details"]',
      title: "Property Details",
      body: "Read-only facts about the property — type, current and future beds/baths, square footage, year built, and MLS number.",
      placement: "top",
    },
    {
      target: '[data-tour="buy-detail-financials"]',
      title: "Financial Summary",
      body: "A read-only snapshot of the deal's numbers — purchase price, ARV, rehab cost, monthly rent and expenses, and annual NOI.",
      placement: "top",
    },
    {
      target: '[data-tour="buy-detail-underwrite"]',
      title: "Copy to Underwriting",
      body: "Copy this listing into Underwriting to run your own co-living numbers. Once copied, the button switches to Review in Underwriting so you can jump back to your model.",
      placement: "bottom",
    },
  ],
};

const acquisitionsGuide: TourConfig = {
  id: "acquisitions",
  steps: [
    {
      target: '[data-tour="acquisitions-heading"]',
      title: "Acquisitions",
      body: "Track every property you're actively pursuing — from initial underwriting through offer, due diligence, and close.",
      placement: "bottom",
    },
    {
      target: '[data-tour="acquisitions-list"]',
      title: "Acquisition pipeline",
      body: "Each card shows the property, current stage, target close date, and purchase price. Click to open the full acquisition record.",
      placement: "top",
    },
    {
      target: '[data-tour="acquisitions-add"]',
      title: "Add an acquisition",
      body: "Click New Acquisition to start tracking a new deal. Link it to an MLS listing or enter the address manually.",
      placement: "bottom",
    },
  ],
};

const acquisitionDetailGuide: TourConfig = {
  id: "acquisition-detail",
  steps: [
    {
      target: '[data-tour="acquisition-detail-heading"]',
      title: "Acquisition Detail",
      body: "The full deal file for one acquisition — pipeline stage, key dates, financials, contacts, and documents all in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="acquisition-detail-progress"]',
      title: "Pipeline progress",
      body: "See where this deal sits across the acquisition stages — from offer through closing. The current stage is highlighted, with completed stages marked green.",
      placement: "bottom",
    },
    {
      target: '[data-tour="acquisition-detail-dates"]',
      title: "Key dates & deadlines",
      body: "Track the critical deadlines — offer date, option period, inspection, appraisal, financing contingency, and closing — each with a countdown so nothing slips.",
      placement: "top",
    },
    {
      target: '[data-tour="acquisition-detail-financials"]',
      title: "Financial summary",
      body: "Offer price, earnest money, option fee, and estimated closing costs roll up into the total estimated cost to close.",
      placement: "top",
    },
    {
      target: '[data-tour="acquisition-detail-docs"]',
      title: "Documents",
      body: "Upload and organize deal documents — purchase agreement, inspection report, appraisal, title commitment, and closing disclosures.",
      placement: "top",
    },
  ],
};

const realtorListingDetailGuide: TourConfig = {
  id: "realtor-listing-detail",
  steps: [
    {
      target: '[data-tour="realtor-listing-detail-heading"]',
      title: "Listing header",
      body: "The address, location, and asking price sit at the top, along with the quick facts you scan first — beds, baths, and square footage.",
      placement: "bottom",
    },
    {
      target: '[data-tour="realtor-listing-detail-photos"]',
      title: "Photos",
      body: "Browse the realtor's photos here. Click any image to open the full-screen gallery, and use the thumbnails or arrows to move between shots.",
      placement: "bottom",
    },
    {
      target: '[data-tour="realtor-listing-detail-suitability"]',
      title: "Co-living suitability",
      body: "This badge tells you how ready the property is for co-living — already operating, suitable as-is, or needing conversion — so you can gauge the work involved before pursuing it.",
      placement: "right",
    },
    {
      target: '[data-tour="realtor-listing-detail-request"]',
      title: "Request a showing",
      body: "Ready to see it in person? Send the listing realtor a showing request — your message opens a conversation in your partner inbox so you can coordinate the visit.",
      placement: "left",
    },
    {
      target: '[data-tour="realtor-listing-detail-underwrite"]',
      title: "Copy to Underwriting",
      body: "Copy this listing straight into Underwriting to run your own co-living numbers. Once copied, the button switches to Review in Underwriting so you can jump back to your model.",
      placement: "left",
    },
  ],
};

const wholesalerListingDetailGuide: TourConfig = {
  id: "wholesaler-listing-detail",
  steps: [
    {
      target: '[data-tour="wholesaler-listing-detail-heading"]',
      title: "Wholesale Deal",
      body: "Full details for one off-market deal submitted by a verified wholesaler partner — the address, asking price, and the headline ARV (After Repair Value) so you can size up the equity spread at a glance.",
      placement: "bottom",
    },
    {
      target: '[data-tour="wholesaler-listing-detail-financials"]',
      title: "Financial summary",
      body: "Compare the asking price against the ARV here — the gap between them is your potential equity spread before rehab. This card also lays out any seller-financing terms: interest rate, loan term, and down payment.",
      placement: "top",
    },
    {
      target: '[data-tour="wholesaler-listing-detail-financing"]',
      title: "Seller financing",
      body: "When a wholesaler offers seller financing, a green Seller Financing badge appears here. That means you may be able to buy without a traditional bank loan — check the Financials card for the exact rate, term, and down payment.",
      placement: "bottom",
    },
    {
      target: '[data-tour="wholesaler-listing-detail-interest"]',
      title: "Express interest",
      body: "Click I'm Interested to send a message straight to the wholesaler. Add a note about your timeline or questions, and their reply lands in your inbox.",
      placement: "left",
    },
    {
      target: '[data-tour="wholesaler-listing-detail-underwrite"]',
      title: "Underwrite it yourself",
      body: "Copy this deal into Underwriting to run your own co-living numbers — gross rents, expenses, and returns. Once copied, this button takes you straight back to the saved model to review it.",
      placement: "left",
    },
  ],
};

const buyBoxSettingsGuide: TourConfig = {
  id: "buy-box-settings",
  steps: [
    {
      target: '[data-tour="buy-box-settings-heading"]',
      title: "Buy Box Settings",
      body: "Define the criteria that make a property worth pursuing — markets, price ranges, bed count, estimated return, and more.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-box-settings-criteria"]',
      title: "Criteria",
      body: "Set minimum and maximum values for price, cap rate, gross yield, room count, and other metrics. Listings that match appear highlighted in the Buy feed.",
      placement: "top",
    },
    {
      target: '[data-tour="buy-box-settings-markets"]',
      title: "Markets",
      body: "Add the cities, counties, or zip codes you actively invest in. The listing feed will prioritize properties in these markets.",
      placement: "top",
    },
  ],
};

const buyBoxReportsGuide: TourConfig = {
  id: "buy-box-reports",
  steps: [
    {
      target: '[data-tour="buy-box-reports-heading"]',
      title: "Buy Box Reports",
      body: "Analyze the listing feed against your buy box criteria to see how many properties are hitting your targets each month.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-box-reports-chart"]',
      title: "Match rate chart",
      body: "See how many listings matched your buy box over time. A rising match rate may indicate a softening market in your target areas.",
      placement: "top",
    },
  ],
};

const buyBoxMatchesGuide: TourConfig = {
  id: "buy-box-matches",
  steps: [
    {
      target: '[data-tour="buy-box-matches-heading"]',
      title: "New matches",
      body: "Newly listed houses your Buy Box Agents found in the last 30 days. Each house shows up once, tagged with every agent that found it, and you can filter by agent and day.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-box-matches-manage"]',
      title: "Your Buy Box Agents",
      body: "Set up to 3 Buy Box Agents on the Agents tab. Each runs every morning around 6am and emails you only when it finds new houses.",
      placement: "bottom",
    },
  ],
};

const buyBoxAgentsGuide: TourConfig = {
  id: "buy-box-agents",
  steps: [
    {
      target: '[data-tour="buy-box-agents-heading"]',
      title: "Buy Box Agents",
      body: "Up to 3 agents each check one city every morning around 6am, looking only at newly listed houses. You're emailed only houses you haven't been sent before.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-box-agents-list"]',
      title: "Your agents",
      body: "Each agent keeps its number (Agent 1, 2, 3), runs one Buy Box version in one city, and looks back 1, 2 or 7 days for new listings. Pause, edit or delete it anytime.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-box-agents-versions"]',
      title: "Buy Box versions",
      body: "Save different criteria sets (V1, V2, …) and pick one per agent. V1 is your Buy Box Settings; underwriting assumptions are shared by every version.",
      placement: "top",
    },
  ],
};

const buyBoxSavedGuide: TourConfig = {
  id: "buy-box-saved",
  steps: [
    {
      target: '[data-tour="buy-box-saved-heading"]',
      title: "Saved Favorites",
      body: "Every listing you've starred across your Buy Box reports lands here, so you can revisit the deals worth a closer look in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="buy-box-saved-list"]',
      title: "Your saved listings",
      body: "Each card shows the key numbers — price, beds/baths, projected cash flow, and cash-on-cash return — pulled from the report that flagged it.",
      placement: "top",
    },
    {
      target: '[data-tour="buy-box-saved-actions"]',
      title: "Take the next step",
      body: "Open the original listing, jump back to its source report, or send it straight into Underwriting to run your own co-living numbers. Remove a favorite once you've ruled it out.",
      placement: "top",
    },
  ],
};

const researchGuide: TourConfig = {
  id: "research",
  steps: [
    {
      target: '[data-tour="research-heading"]',
      title: "Market Research",
      body: "Explore co-living demand, PadSplit statistics, and rental market data for any city to validate a new market before investing.",
      placement: "bottom",
    },
    {
      target: '[data-tour="research-markets"]',
      title: "Market list",
      body: "Each market card shows the number of active PadSplit listings, average weekly rent, and average occupancy rate.",
      placement: "top",
    },
    {
      target: '[data-tour="research-markets"]',
      title: "Search markets",
      body: "Search for any city to pull up its co-living metrics. Click a market to see a detailed breakdown by neighborhood and room type.",
      placement: "bottom",
    },
  ],
};

const researchCityGuide: TourConfig = {
  id: "research-city",
  steps: [
    {
      target: '[data-tour="research-city-heading"]',
      title: "City Research",
      body: "Deep-dive data for a single market — occupancy rates, average rents, demand trends, and comparable properties.",
      placement: "bottom",
    },
    {
      target: '[data-tour="research-city-stats"]',
      title: "Key stats",
      body: "The top metrics show average weekly rent, occupancy, days on market, and total active listings for this city.",
      placement: "bottom",
    },
    {
      target: '[data-tour="research-city-chart"]',
      title: "Trend chart",
      body: "The trend chart shows how rents and occupancy have moved over the past 12 months — useful for spotting seasonal patterns.",
      placement: "top",
    },
  ],
};

const underwritingGuide: TourConfig = {
  id: "underwriting",
  steps: [
    {
      target: '[data-tour="underwriting-heading"]',
      title: "Underwriting",
      body: "Model the financials for any property — projected rents, expenses, NOI, cash-on-cash return, and cap rate — before you make an offer.",
      placement: "bottom",
    },
    {
      target: '[data-tour="underwriting-list"]',
      title: "Saved models",
      body: "All your saved underwriting models are listed here. Each model is linked to a property address and shows the headline return metrics.",
      placement: "top",
    },
    {
      target: '[data-tour="underwriting-new"]',
      title: "New model",
      body: "Click New Model to start a fresh underwriting analysis. Enter the address, purchase price, room count, and projected rents to get an instant return estimate.",
      placement: "bottom",
    },
  ],
};

const underwritingDetailGuide: TourConfig = {
  id: "underwriting-detail",
  steps: [
    {
      target: '[data-tour="underwriting-detail-heading"]',
      title: "Underwriting Model",
      body: "The full financial model for one property — revenue, expenses, debt service, and return metrics calculated automatically as you adjust inputs.",
      placement: "bottom",
    },
    {
      target: '[data-tour="underwriting-detail-inputs"]',
      title: "Inputs",
      body: "Adjust purchase price, renovation budget, room count, weekly rents, vacancy rate, and financing terms to see how each variable affects returns.",
      placement: "top",
    },
    {
      // B19 — the original anchor sits in the Move-to-Renovations dialog, which
      // only exists while Renovations is switched on (shared/hidden-sections.ts);
      // otherwise point at the Key Metrics cash-on-cash figure.
      ...(isHiddenSectionEnabled("renovations")
        ? {
            target: '[data-tour="underwriting-detail-summary"]',
            activate: '[data-testid="button-move-to-renovations"]',
          }
        : { target: '[data-testid="calc-cash-on-cash"]' }),
      title: "Return summary",
      body: "Gross yield, cap rate, cash-on-cash return, and monthly cash flow are calculated in real time. The model highlights figures that fall below your target thresholds.",
      placement: "top",
    },
  ],
};

const underwritingDefaultsGuide: TourConfig = {
  id: "underwriting-defaults",
  steps: [
    {
      target: '[data-tour="underwriting-defaults-heading"]',
      title: "Underwriting Defaults",
      body: "Set the default assumptions used in all new underwriting models — expense ratios, vacancy rates, financing terms, and return targets.",
      placement: "bottom",
    },
    {
      target: '[data-tour="underwriting-defaults-expenses"]',
      title: "Default expense ratios",
      body: "Set percentages for each expense category — property management, repairs, insurance, utilities, and more — so new models start with your real-world averages.",
      placement: "top",
    },
    {
      target: '[data-tour="underwriting-defaults-financing"]',
      title: "Financing defaults",
      body: "Set your typical loan parameters — LTV ratio, interest rate, and amortization period — so the debt service calculation is accurate out of the box.",
      placement: "top",
    },
  ],
};

const pricingGuide: TourConfig = {
  id: "pricing",
  steps: [
    {
      target: '[data-tour="pricing-heading"]',
      title: "Pricing",
      body: "Manage your rent pricing models. Each model defines weekly rates by room type and applies to one or more properties.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pricing-models"]',
      title: "Pricing models",
      body: "Each model card shows the name, room types covered, rate range, and which properties it's applied to.",
      placement: "top",
    },
    {
      target: '[data-tour="pricing-add"]',
      activate: '[data-testid="tab-pricing-model"]',
      title: "Create a model",
      body: "Click New Pricing Model to create a rate structure. Define rates per room type (standard, premium, private bath) and set the effective date.",
      placement: "bottom",
    },
  ],
};

const pricingModelFormGuide: TourConfig = {
  id: "pricing-model-form",
  steps: [
    {
      target: '[data-tour="pricing-model-form-heading"]',
      title: "Pricing Model",
      body: "Define the weekly room rates for a specific pricing model. Rates can vary by room type, floor, or other attributes.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pricing-model-form-rates"]',
      title: "Room rates",
      body: "Set a weekly rate for each room type in this model. The model will apply these rates to all matching rooms on the linked properties.",
      placement: "top",
    },
    {
      target: '[data-tour="pricing-model-form-properties"]',
      title: "Linked properties",
      body: "Choose which properties this model applies to. A property can only have one active pricing model at a time.",
      placement: "top",
    },
  ],
};

const directBookingsGuide: TourConfig = {
  id: "direct-bookings",
  steps: [
    {
      target: '[data-tour="direct-bookings-heading"]',
      title: "Direct Bookings",
      body: "Manage your direct booking landing page — the URL you share with prospects so they can apply to move in without going through PadSplit.",
      placement: "bottom",
    },
    {
      target: '[data-tour="direct-bookings-link"]',
      title: "Your booking link",
      body: "Copy your direct booking URL and share it anywhere — social media, listing sites, or SMS. Applicants fill out the form and their request shows up in Booking Requests.",
      placement: "bottom",
    },
    {
      target: '[data-tour="direct-bookings-settings"]',
      title: "Page settings",
      body: "Customize the headline, description, and available rooms shown on your booking page so prospects see the most relevant information.",
      placement: "top",
    },
  ],
};

const thirdPartyListingsGuide: TourConfig = {
  id: "third-party-listings",
  steps: [
    {
      target: '[data-tour="third-party-listings-heading"]',
      title: "Third-Party Listings",
      body: "Track which rooms are listed on Zillow, Roomies, Craigslist, Facebook Marketplace, and other platforms.",
      placement: "bottom",
    },
    {
      target: '[data-tour="third-party-listings-table"]',
      title: "Listing table",
      body: "Each row shows a room, the platform it's listed on, the listing URL, and whether it's active. Mark listings active or inactive as you publish and pull them.",
      placement: "top",
    },
    {
      target: '[data-tour="third-party-listings-add"]',
      title: "Add a listing",
      body: "Click Add Listing to record a new external listing. Paste the URL so you can jump straight to it from here when you need to update it.",
      placement: "bottom",
    },
    {
      target: '[data-tour="third-party-listings-filters"]',
      title: "Filter by platform",
      body: "Filter by platform to see all your active listings on a specific site at once — useful when a platform changes its requirements.",
      placement: "bottom",
    },
  ],
};

const sellGuide: TourConfig = {
  id: "sell",
  steps: [
    {
      target: '[data-tour="sell-heading"]',
      title: "Sell",
      body: "Track properties you're considering selling or actively listing for sale. Model the disposition financials before you list.",
      placement: "bottom",
    },
    {
      target: '[data-tour="sell-list"]',
      title: "Marketplace, My Listings & Inquiries",
      body: "Use the tabs to switch between the public Marketplace, the listings you've posted (My Listings), and buyer inquiries you've received (My Inquiries).",
      placement: "top",
    },
    {
      target: '[data-tour="sell-tab-mine"]',
      title: "My Listings",
      body: "The My Listings tab shows every property you've put up for sale, with inquiry counts and unread badges so you can see buyer interest at a glance.",
      placement: "bottom",
    },
    {
      target: '[data-tour="sell-tab-inquiries"]',
      title: "My Inquiries",
      body: "The My Inquiries tab is your buyer inbox — open any inquiry to chat with the buyer directly about a listing.",
      placement: "bottom",
    },
    {
      target: '[data-tour="sell-inquiries"]',
      activate: '[data-testid="tab-my-inquiries"]',
      title: "Inquiry workspace",
      body: "Open an inquiry on the left to view the buyer's message thread on the right. Reply, mark threads as read, and follow up without leaving the page.",
      placement: "top",
    },
    {
      target: '[data-tour="sell-add"]',
      title: "Add a property",
      body: "Click New Listing to start tracking a potential sale. Link it to an existing property in your portfolio and enter sale price assumptions.",
      placement: "bottom",
    },
  ],
};

const sellDetailGuide: TourConfig = {
  id: "sell-detail",
  // Owner-only: buyers viewing this same listing see a "Contact Seller" CTA in
  // place of the inquiries inbox, so this step is injected only when the
  // owner-only anchor is on the page. See `ownerScopedStep` on TourConfig.
  ownerScopedStep: {
    target: '[data-tour="sell-detail-chat"]',
    title: "Buyer inquiries",
    body: "Because you own this listing, every buyer who reaches out shows up here. Pick a conversation on the left to read and reply in the chat panel — unread inquiries are badged so you can respond quickly and keep deals moving.",
    placement: "right",
  },
  // Buyer-only: buyers viewing a listing they don't own see the "Contact Seller"
  // CTA instead of the inquiries inbox, so this step is injected only when the
  // buyer-only anchor is on the page. See `buyerScopedStep` on TourConfig.
  buyerScopedStep: {
    target: '[data-tour="sell-detail-contact"]',
    title: "Contact the seller",
    body: "Interested in this property? Use this button to open a private conversation with the seller. Your chat then lives under \"My Inquiries\" on the Buy/Sell page, where you'll see the seller's replies and can keep the conversation going.",
    placement: "left",
  },
  steps: [
    {
      target: '[data-tour="sell-detail-heading"]',
      title: "Listing overview",
      body: "The property name, address, and asking price sit up top, alongside the photo gallery and the seller's description.",
      placement: "bottom",
    },
    {
      target: '[data-tour="sell-detail-model"]',
      title: "Property & financial snapshot",
      body: "Beds, baths, square footage, and asking price are pulled straight from the seller's PadPulse property record. When the seller has published an underwriting analysis, projected performance — monthly cash flow, NOI, cap rate, and gross yield — appears here too.",
      placement: "top",
    },
  ],
};

const financeGuide: TourConfig = {
  id: "finance",
  steps: [
    {
      target: '[data-tour="finance-heading"]',
      title: "Finance",
      body: "Manage financing for your portfolio — track loans, lenders, interest rates, maturity dates, and refinancing opportunities.",
      placement: "bottom",
    },
    {
      target: '[data-tour="finance-loans"]',
      title: "Loan list",
      body: "Each loan card shows the lender, property, outstanding balance, interest rate, maturity date, and monthly payment.",
      placement: "top",
    },
    {
      target: '[data-tour="finance-add"]',
      title: "Add a loan",
      body: "Click Add Loan to record a new financing instrument — mortgage, HELOC, private money, or hard money.",
      placement: "bottom",
    },
  ],
};

const suppliesGuide: TourConfig = {
  id: "supplies",
  propertyScopedStep: {
    target: '[data-testid="link-back-to-property"]',
    title: "Scoped to one property",
    body: "You opened Supplies from a property, so the orders below are already filtered to that property. Widen the property filter to see all properties, or use \"Back to …\" to return to the property.",
    placement: "bottom",
  },
  steps: [
    {
      target: '[data-tour="supplies-heading"]',
      title: "Supplies",
      body: "Track supply orders for your properties — cleaning supplies, toiletries, linens, and other consumables.",
      placement: "bottom",
    },
    {
      target: '[data-tour="supplies-orders"]',
      title: "Order list",
      body: "Each order shows the items, quantity, property, order date, and delivery status. Click an order to see its full line-item detail.",
      placement: "top",
    },
    {
      target: '[data-tour="supplies-add"]',
      title: "New order",
      body: "Click New Order to record a supply purchase. Link it to a property and add the items and quantities so you can track inventory over time.",
      placement: "bottom",
    },
  ],
};

const trashGuide: TourConfig = {
  id: "trash",
  propertyScopedStep: {
    target: '[data-testid="link-back-to-property"]',
    title: "Scoped to one property",
    body: "You opened Trash from a property, so the jobs and on-demand requests below are already focused on that property. Switch tabs or use \"Back to …\" to return to the property.",
    placement: "bottom",
  },
  steps: [
    {
      target: '[data-tour="trash-heading"]',
      title: "Trash Service",
      body: "Manage waste collection across your portfolio — set up scheduled pickups and request one-off, on-demand pickups when something extra needs hauling.",
      placement: "bottom",
    },
    {
      target: '[data-tour="trash-tabs"]',
      title: "Tabs",
      body: "Enrollments lists properties on scheduled service, Jobs tracks individual pickups, On-Demand Requests shows extra haul-aways, Coverage Map shows where service is available, and Billing summarizes the charges.",
      placement: "bottom",
    },
    {
      target: '[data-tour="trash-enrollments"]',
      activate: '[data-testid="tab-enrollments"]',
      title: "Enrollments",
      body: "Each enrolled property shows its collection day, can count, next and last pickup, and a photo of the most recent collection as proof of service.",
      placement: "top",
    },
    {
      target: '[data-tour="trash-enroll"]',
      title: "Enroll a property",
      body: "Click Enroll Property to start scheduled collection — pick the collection day, number of cans, and whether recycling is included.",
      placement: "bottom",
    },
    {
      target: '[data-tour="trash-on-demand"]',
      title: "Request an on-demand pickup",
      body: "Need a one-time haul-away? Click On-Demand to schedule an extra pickup for an eligible property, describe what needs collecting, and confirm billing.",
      placement: "bottom",
    },
  ],
};

const todosGuide: TourConfig = {
  id: "todos",
  steps: [
    {
      target: '[data-tour="todos-heading"]',
      title: "To-Dos",
      body: "Assign internal team to-dos — one-off tasks for you or a teammate. Each one shows up for its assignee under \"Assigned to me\" in the Team Inbox.",
      placement: "bottom",
    },
    {
      target: '[data-tour="todos-filters"]',
      title: "Filter the board",
      body: "Narrow the list by status (Open, Done, or All), by assignee, or by property so you can focus on exactly the to-dos you care about.",
      placement: "bottom",
    },
    {
      target: '[data-tour="todos-board"]',
      title: "The board",
      body: "Each to-do shows its assignee, due date, and linked property — overdue items are flagged in red. Mark one done, reopen it, edit it, or open its comment thread to @-mention a teammate.",
      placement: "top",
    },
    {
      target: '[data-tour="todos-create"]',
      title: "New to-do",
      body: "Click New to-do to create one — give it a name, pick an assignee and an optional property, and set a due date. It lands in their Team Inbox right away.",
      placement: "bottom",
    },
  ],
};

const padsplitSyncGuide: TourConfig = {
  id: "padsplit-sync",
  steps: [
    {
      target: '[data-tour="padsplit-sync-heading"]',
      title: "PadSplit Sync",
      body: "Monitor the status of your PadSplit data synchronization — members, properties, rooms, and messages synced automatically from the PadSplit platform.",
      placement: "bottom",
    },
    {
      target: '[data-tour="padsplit-sync-status"]',
      title: "Sync status",
      body: "Each sync job shows when it last ran, when it will run next, and how many records it processed. A red status means the last sync encountered an error.",
      placement: "top",
    },
    {
      target: '[data-tour="padsplit-sync-trigger"]',
      title: "Manual sync",
      body: "Click Run Now to trigger a manual sync if you need fresh data immediately without waiting for the next scheduled run.",
      placement: "bottom",
    },
  ],
};

const connectPadsplitGuide: TourConfig = {
  id: "connect-padsplit",
  steps: [
    {
      target: '[data-tour="connect-padsplit-heading"]',
      title: "Connect your PadSplit account",
      body: "PadPulse connects to your PadSplit account to bring in your properties, rooms, members and messages, then keeps them up to date.",
      placement: "bottom",
    },
    {
      target: '[data-tour="connect-padsplit-primary"]',
      title: "Start the connection",
      body: "Click Connect PadSplit to open the sync page and link your account. Your first sync starts right after.",
      placement: "bottom",
    },
  ],
};

const imageGeneratorGuide: TourConfig = {
  id: "image-generator",
  steps: [
    {
      target: '[data-tour="image-generator-heading"]',
      title: "Image Generator",
      body: "Generate AI-enhanced staging and marketing photos for your rooms. Upload a raw photo and get a furnished, professionally styled version.",
      placement: "bottom",
    },
    {
      target: '[data-tour="image-generator-rooms"]',
      title: "Room list",
      body: "Select a property and room to see the input photo and any previously generated output images side by side.",
      placement: "top",
    },
    {
      target: '[data-tour="image-generator-generate"]',
      title: "Generate an image",
      body: "Upload a photo, choose a style, and click Generate. The AI will furnish and stage the room. Generated images can be downloaded and used on listing platforms.",
      placement: "top",
    },
  ],
};

const referGuide: TourConfig = {
  id: "refer",
  steps: [
    {
      target: '[data-tour="refer-heading"]',
      title: "Refer & Earn",
      body: "Refer other property managers to PadPulse and earn a reward when they sign up and activate their account.",
      placement: "bottom",
    },
    {
      target: '[data-tour="refer-link"]',
      title: "Your referral link",
      body: "Copy your unique referral link and share it with colleagues, in Facebook groups, or at local real estate meetups.",
      placement: "bottom",
    },
    {
      target: '[data-tour="refer-status"]',
      title: "Referral status",
      body: "Track how many people have clicked your link, signed up, and activated — and see your pending and paid rewards.",
      placement: "top",
    },
  ],
};

const setupGuide: TourConfig = {
  id: "setup",
  steps: [
    {
      target: '[data-tour="setup-heading"]',
      title: "Setup",
      body: "Complete these setup steps to get the most out of PadPulse. Each card links to the relevant settings page.",
      placement: "bottom",
    },
    {
      target: '[data-tour="setup-checklist"]',
      title: "Setup checklist",
      body: "Work through the checklist in order — add your first property, invite your team, connect your smart devices, and configure your pricing model.",
      placement: "top",
    },
    {
      target: '[data-tour="setup-progress"]',
      title: "Progress",
      body: "The progress bar shows how far along you are. Completing more steps unlocks additional features and improves your portfolio visibility.",
      placement: "bottom",
    },
  ],
};

const propertyLaunchGuide: TourConfig = {
  id: "property-launch",
  steps: [
    {
      target: '[data-tour="property-launch-heading"]',
      title: "Property Launch",
      body: "Walk through every step to take a new property from acquisition to fully listed and accepting members.",
      placement: "bottom",
    },
    {
      target: '[data-tour="property-launch-steps"]',
      title: "Launch checklist",
      body: "Each step covers a key milestone — room setup, pricing, listing platforms, member intake form, and device installation. Check each one off as you complete it.",
      placement: "top",
    },
    {
      target: '[data-tour="property-launch-progress"]',
      title: "Progress bar",
      body: "The progress bar shows what percentage of launch steps are complete. Share a link to this page with your team so everyone can see what's left.",
      placement: "bottom",
    },
  ],
};

const messageRuleFormGuide: TourConfig = {
  id: "message-rule-form",
  steps: [
    {
      target: '[data-tour="message-rule-form-heading"]',
      title: "Message Rule",
      body: "Configure an automated message that sends based on a rule you define — an event, a schedule, or an incoming keyword. Fill in each section below to set it up.",
      placement: "bottom",
    },
    {
      target: '[data-tour="message-rule-form-property-selector"]',
      title: "Send this message to",
      body: "Choose which properties this rule applies to. Select all properties or pick specific ones — you can create different rules per property if needed.",
      placement: "bottom",
    },
    {
      target: '[data-tour="message-rule-form-rule-type"]',
      title: "Select a rule type and complete the rule",
      body: "Pick the type of rule: event-based (triggered by a member action like move-in or missed payment), scheduled (sent on a repeating schedule), or keyword-based (triggered by incoming message keywords). Once you select a type, fill in the required fields that appear — such as the event, timing, schedule, or keywords.",
      placement: "bottom",
    },
    {
      target: '[data-tour="message-rule-form-template"]',
      title: "Message template",
      body: "Write the message body. Use merge tags like {{member_name}} and {{property_name}} to personalize each message automatically.",
      placement: "top",
    },
    {
      target: '[data-tour="message-rule-form-approval"]',
      title: "Message Approval",
      body: "Choose how messages are handled before delivery. Send automatically sends the message without any review — either instantly when the trigger fires or after a delay you set. Review before sending creates a draft and notifies you by email or SMS so you can approve or edit each message before it goes out.",
      placement: "top",
    },
  ],
};

const pulseGuide: TourConfig = {
  id: "daily-pulse",
  steps: [
    {
      target: '[data-tour="pulse-heading"]',
      title: "Your Daily Pulse",
      body: "Pulse is your AI-powered morning briefing. Every day it reads your inbox, reviews, financials, and bookings and surfaces the 3–8 things that actually need your attention — ranked by priority.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pulse-tell-it-to-me"]',
      title: '"Tell it to me"',
      body: 'Click this button to hear your entire briefing read aloud. Pulse picks your preferred voice and streams the audio directly in the browser — great for a hands-free morning walk-through.',
      placement: "bottom",
    },
    {
      target: '[data-tour="pulse-item-feedback"]',
      title: "Vote to train Pulse",
      body: "Thumbs-up an item when it was relevant, thumbs-down when it wasn't. Your votes teach Pulse what to surface more (or less) often so the briefing gets sharper over time.",
      placement: "top",
    },
    {
      target: '[data-tour="pulse-settings-trigger"]',
      title: "Settings",
      body: "Open Settings to toggle the daily email digest (get your briefing delivered to your inbox each morning) and to pick the voice Pulse uses when you tap \"Tell it to me.\"",
      placement: "left",
    },
    {
      target: '[data-tour="pulse-controls"]',
      title: "Refresh & collapse",
      body: "Hit Refresh to ask Pulse to re-read your data and generate a fresh briefing. Use the eye icon to collapse the widget when you want more screen space.",
      placement: "bottom",
    },
  ],
};

// Sample-preview variant shown to lower-tier hosts on the Daily Pulse page.
// The full `daily-pulse` guide above references controls that only exist on the
// real briefing (feedback voting, settings, refresh/collapse). The sample
// preview only has the heading, a "Tell it to me - sample" button that plays a
// short spoken sample, sample items, and an upgrade CTA — so it gets its own
// steps that match what's on screen and frame the upgrade.
const pulseSampleGuide: TourConfig = {
  id: "daily-pulse-sample",
  steps: [
    {
      target: '[data-tour="pulse-heading"]',
      title: "A taste of Daily Pulse",
      body: "This is a sample preview of Pulse — your AI-powered morning briefing. On the Professional plan, Pulse reads your real inbox, reviews, financials, and bookings every morning and surfaces the few things that actually need your attention.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pulse-tell-it-to-me"]',
      title: '"Tell it to me - sample"',
      body: "Tap this to hear a short spoken sample of your morning briefing. On Professional, this button reads your entire real briefing aloud in your preferred voice.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pulse-sample-items"]',
      title: "Example signals",
      body: "These are sample items showing the kinds of things Pulse flags — ranked by priority and tagged by category. With Professional, these would be drawn from your own live data instead of examples.",
      placement: "top",
    },
    {
      target: '[data-tour="pulse-sample-upgrade"]',
      title: "Unlock your real Pulse",
      body: "Upgrade to Professional to turn this preview into your own daily briefing built from your real inbox, reviews, financials, and bookings.",
      placement: "top",
    },
  ],
};

const connectionsGuide: TourConfig = {
  id: "connections",
  steps: [
    {
      target: '[data-tour="connections-heading"]',
      title: "Connections",
      body: "This is your unified inbox for every conversation with partners and other hosts — lenders, realtors, brokers, wholesalers, and marketplace buyers and sellers — all in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="connections-inbox"]',
      title: "Your inbox",
      body: "Each row is a separate conversation, sorted by the most recent message. A blue badge shows unread messages. Click any conversation to open it in the panel on the right.",
      placement: "right",
    },
    {
      target: '[data-tour="connections-detail"]',
      title: "The conversation",
      body: "The selected conversation opens here. Read the full message history and reply inline — the counterparty and conversation type are always shown at the top for context.",
      placement: "left",
    },
  ],
};

const partnerConversationsGuide: TourConfig = {
  id: "partner-conversations",
  steps: [
    {
      target: '[data-tour="partner-conversations-heading"]',
      title: "Partner Conversations",
      body: "This is where you read and reply to messages with the partners (lenders, brokers, realtors, and wholesalers) connected to your account.",
      placement: "bottom",
    },
    {
      target: '[data-tour="partner-conversations-card"]',
      title: "Your conversations",
      body: "Pick a thread on the left to open it, then read the history and send a reply on the right. New messages surface here automatically.",
      placement: "top",
    },
    {
      target: '[data-tour="partner-conversations-compliance"]',
      title: "Monitored for your safety",
      body: "Partner conversations are monitored by PadPulse. Keep discussions professional — this protects both you and the partners you work with.",
      placement: "top",
    },
  ],
};

const reviewsGuide: TourConfig = {
  id: "reviews",
  steps: [
    {
      target: '[data-tour="reviews-heading"]',
      title: "PadSplit Reviews",
      body: "Track member stay reviews and move-in reviews across your portfolio, all in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="reviews-summary"]',
      title: "At-a-glance metrics",
      body: "These tiles summarize your review volume, average rating, and the split of positive vs. negative feedback over the last 30 days.",
      placement: "bottom",
    },
    {
      target: '[data-tour="reviews-filters"]',
      title: "Filter the reviews",
      body: "Narrow down by property, sentiment, rating, or date range — or search the text directly — to find exactly the feedback you care about.",
      placement: "bottom",
    },
    {
      target: '[data-tour="reviews-list"]',
      title: "Browse reviews",
      body: "Switch between All, Stay, and Move-In reviews to read individual member feedback card by card.",
      placement: "top",
    },
    {
      target: '[data-tour="reviews-notifications"]',
      title: "Manage notifications",
      body: "Open the Manage section to control how you get alerted about new or low-rated reviews.",
      placement: "bottom",
    },
  ],
};

const pmBillingGuide: TourConfig = {
  id: "pm-billing",
  steps: [
    {
      target: '[data-tour="pm-billing-heading"]',
      title: "Owner Settings",
      body: "Configure how you bill the property owners you manage — your management fee and any vendor upcharges.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pm-billing-mgmt-fee"]',
      title: "Management fee",
      body: "Set your management fee type and amount. This is what you charge owners for managing their properties.",
      placement: "bottom",
    },
    {
      target: '[data-tour="pm-billing-vendor-upcharge"]',
      title: "Vendor upcharge",
      body: "Add a global markup on vendor costs, with optional per-category overrides, so maintenance work is billed to owners at your rate.",
      placement: "top",
    },
    {
      target: '[data-tour="pm-billing-save"]',
      title: "Save your settings",
      body: "Click Save Owner Settings to apply your changes. They take effect on future owner invoices.",
      placement: "top",
    },
    {
      target: '[data-tour="pm-billing-effective-rates"]',
      title: "Effective rates by owner",
      body: "This table shows the merged rates that actually apply to each owner after defaults and overrides.",
      placement: "top",
    },
  ],
};

const accountKnowledgeBaseGuide: TourConfig = {
  id: "account-knowledge-base",
  steps: [
    {
      target: '[data-tour="account-knowledge-base-heading"]',
      title: "Account Knowledge Base",
      body: "Store account-wide context — policies, procedures, and facts — that PadPulse's AI uses when drafting messages and answering questions.",
      placement: "bottom",
    },
    {
      target: '[data-tour="account-knowledge-base-property-pointer"]',
      title: "Property-specific knowledge",
      body: "Each property has its own knowledge base too. Use the quick links here to jump to a specific property's details.",
      placement: "bottom",
    },
    {
      target: '[data-tour="account-knowledge-base-content"]',
      title: "Edit and AI memory",
      body: "Edit your account knowledge here and review the AI Memory section to see what the assistant has learned over time.",
      placement: "top",
    },
  ],
};

const accountMessagingGuide: TourConfig = {
  id: "account-messaging",
  steps: [
    {
      target: '[data-tour="account-messaging-heading"]',
      title: "Messaging Number",
      body: "Manage the dedicated phone number your account uses to send and receive text messages with members.",
      placement: "bottom",
    },
    {
      target: '[data-tour="account-messaging-current"]',
      title: "Your current number",
      body: "See the number assigned to your account, its status, and options to replace or release it.",
      placement: "top",
    },
    {
      target: '[data-tour="account-messaging-billing-link"]',
      title: "Back to billing",
      body: "Messaging is part of your subscription. Use this link to return to Billing to review or change your plan.",
      placement: "top",
    },
  ],
};

// Quo messaging — Messages → Settings → Messaging provider.
const messagesSettingsGuide: TourConfig = {
  id: "messages-settings",
  steps: [
    {
      target: '[data-tour="messages-settings-heading"]',
      title: "Messaging provider",
      body: "Choose how texts to your direct-booked members travel: your Quo (OpenPhone) inbox or a dedicated Twilio number. One provider is active at a time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-settings-provider"]',
      title: "Active provider",
      body: "See which provider is live right now and switch between Quo and Twilio once both are set up.",
      placement: "top",
    },
    {
      target: '[data-tour="messages-settings-quo"]',
      title: "Connect Quo",
      body: "Paste a Quo API key, pick the inbox to send from, and PadPulse imports your existing member conversations and keeps them in sync.",
      placement: "top",
    },
  ],
};

// Messages Auto Draft (private beta) — Messages → Settings → Auto Draft.
const messagesAutoDraftGuide: TourConfig = {
  id: "messages-auto-draft",
  steps: [
    {
      target: '[data-tour="messages-auto-draft-heading"]',
      title: "Auto Draft",
      body: "Let AI write a reply to every new member message. The draft waits in that member's chat, clearly labelled as an auto-draft.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-auto-draft-settings"]',
      title: "Switch and send delay",
      body: "Turn Auto Draft on for the whole account and pick how long a draft waits before sending itself. Choose Never to send every draft yourself.",
      placement: "top",
    },
    {
      target: '[data-tour="messages-auto-draft-rules-first"]',
      title: "Rules come first",
      body: "Your message rules always win: when a rule replies or queues a message, no auto-draft is made, and a waiting draft is cancelled.",
      placement: "top",
    },
  ],
};

// Messages → Unmatched texts (triage for SMS from unknown numbers).
const messagesUnmatchedSmsGuide: TourConfig = {
  id: "messages-unmatched-sms",
  steps: [
    {
      target: '[data-tour="messages-unmatched-heading"]',
      title: "Unmatched texts",
      body: "Texts that reached your messaging number from a phone PadPulse couldn't match to a member wait here instead of being dropped.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-unmatched-filter"]',
      title: "Pending, assigned, ignored",
      body: "Pending texts need a decision. Assigned ones already live in a member's conversation; ignored ones can be reopened.",
      placement: "bottom",
    },
    {
      target: '[data-tour="messages-unmatched-list"]',
      title: "Assign or ignore",
      body: "Assign a text to the right member (optionally saving the sender's number on their profile so future texts match), or ignore it.",
      placement: "top",
    },
  ],
};

const operationsOwnersGuide: TourConfig = {
  id: "operations-owners",
  steps: [
    {
      target: '[data-tour="operations-owners-heading"]',
      title: "Owners",
      body: "Manage the property owners you work with and review their portfolio financials in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-owners-add"]',
      title: "Add an owner",
      body: "Use Add Owner to onboard a new property owner and link their properties to your management account.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-owners-period"]',
      title: "Financials period",
      body: "Pick the date range here to control the period the owner financials below are calculated for.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-owners-grid"]',
      title: "Your owners",
      body: "Each card summarizes an owner and their properties. Click into one to see detailed financials and settings.",
      placement: "top",
    },
  ],
};

const operationsOwnerInvoicesGuide: TourConfig = {
  id: "operations-owner-invoices",
  steps: [
    {
      target: '[data-tour="operations-owner-invoices-heading"]',
      title: "Owner Invoices",
      body: "Create, send, and track the invoices you bill to the property owners you manage.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-owner-invoices-generate"]',
      title: "Generate invoices",
      body: "Use Generate Invoices to draft new owner invoices for a period based on your management fees and vendor charges.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-owner-invoices-summary"]',
      title: "Status at a glance",
      body: "These cards show how many invoices are still in draft and how much is outstanding versus already collected.",
      placement: "bottom",
    },
    {
      target: '[data-tour="operations-owner-invoices-tabs"]',
      title: "Filter by status",
      body: "Switch between All, Draft, Sent, Paid, and Overdue to focus on the invoices that need your attention.",
      placement: "bottom",
    },
  ],
};

const portfolioAnalyticsGuide: TourConfig = {
  id: "portfolio-analytics",
  steps: [
    {
      target: '[data-tour="portfolio-analytics-heading"]',
      title: "Portfolio Analytics",
      body: "Dig into performance trends across your whole portfolio — income, member health, and room performance.",
      placement: "bottom",
    },
    {
      target: '[data-tour="portfolio-analytics-tabs"]',
      title: "Switch perspectives",
      body: "Use these tabs to move between AI Insights, Overview, Income Analysis, Member Health, Room Performance, and Settings.",
      placement: "bottom",
    },
    {
      target: '[data-tour="portfolio-analytics-insights"]',
      title: "AI insights",
      body: "The AI Insights tab opens by default and surfaces the most important takeaways and recommendations automatically.",
      placement: "top",
    },
  ],
};

const teamStatsGuide: TourConfig = {
  id: "team-stats",
  steps: [
    {
      target: '[data-tour="team-stats-heading"]',
      title: "Team Stats",
      body: "See how your account and each team member are performing — response times, resolves, and task throughput in one place.",
      placement: "bottom",
    },
    {
      target: '[data-tour="team-stats-tabs"]',
      title: "Day, Trends, and Tickets",
      body: "Pick a specific day to see who did what, watch trends over weeks or months, and check how fast tickets get touched and closed by category.",
      placement: "bottom",
    },
    {
      target: '[data-tour="team-stats-day"]',
      title: "Leaderboard & SLA",
      body: "The leaderboard ranks your team by the metric you choose in Settings, and flags anyone over your SLA targets so you know who may need support.",
      placement: "top",
    },
  ],
};

const findRealtorGuide: TourConfig = {
  id: "find-realtor",
  steps: [
    {
      target: '[data-tour="find-realtor-heading"]',
      title: "Find a Realtor",
      body: "Browse realtors who work with co-living investors and connect with one that fits your market.",
      placement: "bottom",
    },
    {
      target: '[data-tour="find-realtor-filter"]',
      title: "Filter by state",
      body: "Use the state filter to narrow the list to realtors who operate where you're looking to buy.",
      placement: "bottom",
    },
    {
      target: '[data-tour="find-realtor-table"]',
      title: "The realtor directory",
      body: "Each row is a realtor with their coverage area and details so you can compare options.",
      placement: "top",
    },
    {
      target: '[data-tour="find-realtor-connect"]',
      title: "Connect with a realtor",
      body: "Click Work with to start a conversation with a realtor you'd like to partner with.",
      placement: "left",
    },
  ],
};

const furnishingAddressBookGuide: TourConfig = {
  id: "furnishing-address-book",
  steps: [
    {
      target: '[data-tour="furnishing-address-book-heading"]',
      title: "Address Book",
      body: "Save the shipping addresses you use most so furnishing orders go to the right place every time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="furnishing-address-book-add"]',
      title: "Add an address",
      body: "Use Add Address to save a new property or warehouse address with a memorable nickname.",
      placement: "bottom",
    },
    {
      target: '[data-tour="furnishing-address-book-search"]',
      title: "Find an address fast",
      body: "Search by nickname, label, street, or city to quickly locate a saved address.",
      placement: "bottom",
    },
  ],
};

const faqGuide: TourConfig = {
  id: "faq",
  steps: [
    {
      target: '[data-tour="faq-heading"]',
      title: "Host FAQ",
      body: "Answers to the most common questions hosts ask about using PadPulse, all on one page.",
      placement: "bottom",
    },
    {
      target: '[data-tour="faq-share"]',
      title: "Share the FAQ",
      body: "Use Share FAQ to copy a link you can send to a teammate or co-host.",
      placement: "left",
    },
  ],
};

// ─── Registry ────────────────────────────────────────────────────────────────

export const APP_GUIDE_REGISTRY: Record<string, TourConfig> = {
  [dashboardGuide.id]: dashboardGuide,
  [messagesSettingsGuide.id]: messagesSettingsGuide,
  [messagesAutoDraftGuide.id]: messagesAutoDraftGuide,
  [messagesUnmatchedSmsGuide.id]: messagesUnmatchedSmsGuide,
  [cleaningGuide.id]: cleaningGuide,
  [connectionsGuide.id]: connectionsGuide,
  [propertiesGuide.id]: propertiesGuide,
  [propertyDetailGuide.id]: propertyDetailGuide,
  [messagesGuide.id]: messagesGuide,
  [financialsGuide.id]: financialsGuide,
  [membersGuide.id]: membersGuide,
  [maintenanceGuide.id]: maintenanceGuide,
  [vendorsGuide.id]: vendorsGuide,
  [devicesGuide.id]: devicesGuide,
  [teamGuide.id]: teamGuide,
  [collectionsGuide.id]: collectionsGuide,
  [maintenanceSettingsGuide.id]: maintenanceSettingsGuide,
  [billingGuide.id]: billingGuide,
  [accountPlanGuide.id]: accountPlanGuide,
  [profileGuide.id]: profileGuide,
  [smartLocksGuide.id]: smartLocksGuide,
  [thermostatsGuide.id]: thermostatsGuide,
  [memberDetailGuide.id]: memberDetailGuide,
  [bookingRequestsGuide.id]: bookingRequestsGuide,
  [bookingRequestSettingsGuide.id]: bookingRequestSettingsGuide,
  [vehiclesGuide.id]: vehiclesGuide,
  [vendorDetailGuide.id]: vendorDetailGuide,
  [vendorSettingsGuide.id]: vendorSettingsGuide,
  [vendorMarketplaceGuide.id]: vendorMarketplaceGuide,
  [vendorPaymentsGuide.id]: vendorPaymentsGuide,
  [operationsPaymentsGuide.id]: operationsPaymentsGuide,
  [serviceFormsGuide.id]: serviceFormsGuide,
  [operationsTaskRulesGuide.id]: operationsTaskRulesGuide,
  [furnishingGuide.id]: furnishingGuide,
  [furnishingPropertyGuide.id]: furnishingPropertyGuide,
  [renovationsGuide.id]: renovationsGuide,
  [buyGuide.id]: buyGuide,
  [buyDetailGuide.id]: buyDetailGuide,
  [acquisitionsGuide.id]: acquisitionsGuide,
  [acquisitionDetailGuide.id]: acquisitionDetailGuide,
  [realtorListingDetailGuide.id]: realtorListingDetailGuide,
  [wholesalerListingDetailGuide.id]: wholesalerListingDetailGuide,
  [buyBoxSettingsGuide.id]: buyBoxSettingsGuide,
  [buyBoxReportsGuide.id]: buyBoxReportsGuide,
  [buyBoxMatchesGuide.id]: buyBoxMatchesGuide,
  [buyBoxAgentsGuide.id]: buyBoxAgentsGuide,
  [buyBoxSavedGuide.id]: buyBoxSavedGuide,
  [researchGuide.id]: researchGuide,
  [researchCityGuide.id]: researchCityGuide,
  [underwritingGuide.id]: underwritingGuide,
  [underwritingDetailGuide.id]: underwritingDetailGuide,
  [underwritingDefaultsGuide.id]: underwritingDefaultsGuide,
  [pricingGuide.id]: pricingGuide,
  [pricingModelFormGuide.id]: pricingModelFormGuide,
  [directBookingsGuide.id]: directBookingsGuide,
  [thirdPartyListingsGuide.id]: thirdPartyListingsGuide,
  [sellGuide.id]: sellGuide,
  [sellDetailGuide.id]: sellDetailGuide,
  [financeGuide.id]: financeGuide,
  [suppliesGuide.id]: suppliesGuide,
  [trashGuide.id]: trashGuide,
  [todosGuide.id]: todosGuide,
  [padsplitSyncGuide.id]: padsplitSyncGuide,
  [connectPadsplitGuide.id]: connectPadsplitGuide,
  [imageGeneratorGuide.id]: imageGeneratorGuide,
  [referGuide.id]: referGuide,
  [setupGuide.id]: setupGuide,
  [propertyLaunchGuide.id]: propertyLaunchGuide,
  [messageRuleFormGuide.id]: messageRuleFormGuide,
  [pulseGuide.id]: pulseGuide,
  [pulseSampleGuide.id]: pulseSampleGuide,
  [partnerConversationsGuide.id]: partnerConversationsGuide,
  [reviewsGuide.id]: reviewsGuide,
  [pmBillingGuide.id]: pmBillingGuide,
  [accountKnowledgeBaseGuide.id]: accountKnowledgeBaseGuide,
  [accountMessagingGuide.id]: accountMessagingGuide,
  [operationsOwnersGuide.id]: operationsOwnersGuide,
  [operationsOwnerInvoicesGuide.id]: operationsOwnerInvoicesGuide,
  [portfolioAnalyticsGuide.id]: portfolioAnalyticsGuide,
  [teamStatsGuide.id]: teamStatsGuide,
  [findRealtorGuide.id]: findRealtorGuide,
  [furnishingAddressBookGuide.id]: furnishingAddressBookGuide,
  [faqGuide.id]: faqGuide,
  // ── Admin section guides ──────────────────────────────────────────────────
  ...Object.fromEntries(ALL_ADMIN_GUIDES.map((g) => [g.id, g])),
};
